import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
const DEFAULT_TIMEZONE_LABEL = 'Brasil/Brasilia';

const TIMEZONE_OPTIONS = [
  { value: 'America/Sao_Paulo', label: 'Brasil/Brasilia (BRT)' },
  { value: 'America/Manaus', label: 'Brasil/Manaus (AMT)' },
  { value: 'America/Cuiaba', label: 'Brasil/Cuiaba (AMT)' },
  { value: 'America/Belem', label: 'Brasil/Belem (BRT)' },
  { value: 'UTC', label: 'UTC' },
];

const TimezoneContext = createContext(null);

const asDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [timezoneLabel, setTimezoneLabel] = useState(DEFAULT_TIMEZONE_LABEL);
  const [loading, setLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setTimezone(DEFAULT_TIMEZONE);
      setTimezoneLabel(DEFAULT_TIMEZONE_LABEL);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/settings/timezone`);
      setTimezone(res.data?.timezone || DEFAULT_TIMEZONE);
      setTimezoneLabel(res.data?.timezone_label || DEFAULT_TIMEZONE_LABEL);
    } catch (error) {
      console.log('Using default timezone settings');
      setTimezone(DEFAULT_TIMEZONE);
      setTimezoneLabel(DEFAULT_TIMEZONE_LABEL);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(async (nextTimezone) => {
    try {
      const res = await axios.put(`${API}/settings/timezone`, { timezone: nextTimezone });
      setTimezone(res.data?.timezone || nextTimezone);
      setTimezoneLabel(res.data?.timezone_label || DEFAULT_TIMEZONE_LABEL);
      return true;
    } catch (error) {
      console.error('Error saving timezone settings:', error);
      return false;
    }
  }, []);

  const getTimezoneAbbreviation = useCallback((value) => {
    const date = asDate(value) || new Date();
    const parts = new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).formatToParts(date);
    return parts.find((part) => part.type === 'timeZoneName')?.value || timezone;
  }, [timezone]);

  const formatDateTime = useCallback((value, options = {}) => {
    const date = asDate(value);
    if (!date) return '--';
    const { omitSeconds = false, ...intlOptions } = options;
    const formatted = new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(omitSeconds ? {} : { second: '2-digit' }),
      hour12: false,
      ...intlOptions,
    }).format(date);
    return `${getTimezoneAbbreviation(date)} ${formatted}`;
  }, [getTimezoneAbbreviation, timezone]);

  const formatDate = useCallback((value, options = {}) => {
    const date = asDate(value);
    if (!date) return '--';
    const formatted = new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...options,
    }).format(date);
    return `${getTimezoneAbbreviation(date)} ${formatted}`;
  }, [getTimezoneAbbreviation, timezone]);

  const formatTime = useCallback((value, options = {}) => {
    const date = asDate(value);
    if (!date) return '--';
    const { omitSeconds = false, ...intlOptions } = options;
    const formatted = new Intl.DateTimeFormat('pt-BR', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      ...(omitSeconds ? {} : { second: '2-digit' }),
      hour12: false,
      ...intlOptions,
    }).format(date);
    return `${getTimezoneAbbreviation(date)} ${formatted}`;
  }, [getTimezoneAbbreviation, timezone]);

  const value = useMemo(() => ({
    timezone,
    timezoneLabel,
    timezoneOptions: TIMEZONE_OPTIONS,
    loading,
    saveSettings,
    formatDateTime,
    formatDate,
    formatTime,
    getTimezoneAbbreviation,
  }), [
    timezone,
    timezoneLabel,
    loading,
    saveSettings,
    formatDateTime,
    formatDate,
    formatTime,
    getTimezoneAbbreviation,
  ]);

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};
